import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import en from 'date-fns/locale/en-US';
import ja from 'date-fns/locale/ja';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PlayerSelect } from '@/components/players/PlayerSelect';
import { SupplierType } from '@/lib/constants/suppliers';

// Program form type definition
export interface ProgramFormData {
  since: Date | null;
  duration: string;
  supplier: string;
  playerId?: number;
}

// Validation error type definition
export interface ProgramFormErrors {
  since?: string;
  duration?: string;
  supplier?: string;
  playerId?: string;
}

interface ProgramFormProps {
  initialData: ProgramFormData;
  onSubmit: (formData: ProgramFormData) => Promise<void>;
  submitLabel: string;
  loading: boolean;
}

export function ProgramForm({ initialData, onSubmit, submitLabel, loading }: ProgramFormProps) {
  const { t, i18n } = useTranslation(['programs', 'common']);
  const [formData, setFormData] = useState<ProgramFormData>(initialData);
  const [errors, setErrors] = useState<ProgramFormErrors>({});

  // Update form data when initial data changes
  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  // Supplier selection change handler
  const handleSupplierChange = (event: SelectChangeEvent) => {
    setFormData({
      ...formData,
      supplier: event.target.value,
    });
    // Clear error
    if (errors.supplier) {
      setErrors({
        ...errors,
        supplier: undefined,
      });
    }
  };

  // Player selection change handler
  const handlePlayerChange = (playerId: number | undefined) => {
    setFormData({
      ...formData,
      playerId,
    });
    // Clear error
    if (errors.playerId) {
      setErrors({
        ...errors,
        playerId: undefined,
      });
    }
  };

  // Start date/time change handler
  const handleSinceChange = (date: Date | null) => {
    setFormData({
      ...formData,
      since: date,
    });
    // Clear error
    if (errors.since) {
      setErrors({
        ...errors,
        since: undefined,
      });
    }
  };

  // Duration (seconds) change handler
  const handleDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow only numeric values
    if (value === '' || /^\d+$/.test(value)) {
      setFormData({
        ...formData,
        duration: value,
      });
      // Clear error
      if (errors.duration) {
        setErrors({
          ...errors,
          duration: undefined,
        });
      }
    }
  };

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: ProgramFormErrors = {};

    if (!formData.since) {
      newErrors.since = t('programs:validation.startDateRequired');
    }

    if (!formData.duration) {
      newErrors.duration = t('programs:validation.durationRequired');
    } else if (!/^\d+$/.test(formData.duration)) {
      newErrors.duration = t('programs:validation.durationNumeric');
    } else if (parseInt(formData.duration, 10) <= 0) {
      newErrors.duration = t('programs:validation.durationPositive');
    }

    if (!formData.supplier) {
      newErrors.supplier = t('programs:validation.supplierRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await onSubmit(formData);
  };

  // Calculate end date/time from start date/time and duration
  const calculateUntilDate = (): Date | null => {
    if (!formData.since || !formData.duration) {
      return null;
    }

    const sinceDate = new Date(formData.since);
    const durationInSeconds = parseInt(formData.duration, 10);
    return new Date(sinceDate.getTime() + durationInSeconds * 1000);
  };

  const untilDate = calculateUntilDate();

  // Select date-fns locale based on current language
  const dateLocale = i18n.language === 'ja' ? ja : en;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
      <form onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
          <DateTimePicker
            label={t('programs:form.startDate')}
            value={formData.since}
            onChange={handleSinceChange}
            views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
            ampm={false}
            slotProps={{
              textField: {
                fullWidth: true,
                error: !!errors.since,
                helperText: errors.since,
              },
            }}
          />
        </Grid>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
          <TextField
            label={t('programs:form.duration')}
            value={formData.duration}
            onChange={handleDurationChange}
            fullWidth
            error={!!errors.duration}
            helperText={errors.duration}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          />
        </Grid>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
          <FormControl fullWidth error={!!errors.supplier}>
            <InputLabel id="supplier-label">{t('programs:form.supplier')}</InputLabel>
            <Select
              labelId="supplier-label"
              value={formData.supplier}
              label={t('programs:form.supplier')}
              onChange={handleSupplierChange}
            >
              <MenuItem value={SupplierType.HIVESTACK}>Hivestack</MenuItem>
            </Select>
            {errors.supplier && (
              <FormHelperText>{errors.supplier}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '33.33%' } }}>
          <PlayerSelect
            value={formData.playerId}
            onChange={handlePlayerChange}
            error={errors.playerId}
            fullWidth
          />
        </Grid>
        {untilDate && (
          <Grid component="div" sx={{ width: '100%' }}>
            <Typography variant="body2" color="textSecondary">
              {t('programs:form.endDate')}: {untilDate.toLocaleString()}
            </Typography>
          </Grid>
        )}
      </Grid>

        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            {loading ? <CircularProgress size={24} /> : submitLabel}
          </Button>
        </Box>
      </form>
    </LocalizationProvider>
  );
}

export default ProgramForm;
